// Entry of the TC React prototype: hash routing, theme preference, the controlled detail peek, and the shell demo data.
// Pages come from the two domain modules through PageProps. Everything is local: no message, payment or network action.
import "../vendor/ubm-ui-components/src/styles/foundation.css";
import { StrictMode, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createRoot } from "react-dom/client";
import type { CommandItem } from "../vendor/ubm-ui-components/src/components/command-palette";
import type { NotificationItem } from "../vendor/ubm-ui-components/src/components/notification-center";
import { createThemeVariables, type ThemeConfig } from "../vendor/ubm-ui-components/src/theme";
import { ActivitiesPages, useActivities } from "./activities";
import { LEVELS, STEPS } from "./activities-fixtures";
import { ExchangePages, exchangeAccountSource, exchangeCoachingStore, type ExchangeAccountState } from "./exchanges";
import { MILES_JOURNAL } from "./exchanges-fixtures";
import { CoachingExperience, connectCoachingStore } from "./coaching";
import {
  ShopAccountExperience, connectAccountExperienceStore, openAccountSection,
  type AccountExperienceSnapshot, type AccountExperienceStore,
} from "./account-experience";
import { LearnExperience } from "./learning";
import type { PageProps, PeekDetail, Route } from "./contracts";
import { Shell, routeCommands, type ShellPerson, type ThemePreference } from "./Shell";
import { MOBILE_QUERY, SharedPeek, useMediaQuery } from "./shared";
import "./shell.css";
import "./tc-theme.css";

// Coaching reads the Échanges store through its adapter (same balances, appointments, leads, catch-ups as Boutique and
// Miles): connected once, at module load, outside any component.
connectCoachingStore(exchangeCoachingStore);

const ROUTES: readonly Route[] = ["home", "agir", "learn", "accompagnement", "liberty", "community", "team", "shop", "account", "miles"];
const ACTIVITY_ROUTES = new Set<Route>(["home", "agir", "learn", "liberty"]);

const isRoute = (value: string): value is Route => (ROUTES as readonly string[]).includes(value);
/** The route is the hash path; a query (#/learn?level=L1&lesson=l2) belongs to the page and is stripped here. */
const readRoute = (): Route => { const value = window.location.hash.replace(/^#\/?/, "").split("?")[0] ?? ""; return isRoute(value) ? value : "home"; };
const hashHasQuery = () => window.location.hash.includes("?");

/* ---------- Shell demo data, taken from the existing prototype stores (Team threads, Community news, Miles journal). ---------- */

/** Current level = level of the step holding the last lesson followed in Apprendre (live: learn.last of the activities store). */
const levelOfLesson = (lessonId: string) => LEVELS.find(level => level.id === Object.values(STEPS).find(step => step.lessons.includes(lessonId))?.level)?.title;
const milesBalance = (journal: AccountExperienceSnapshot["milesJournal"]) => journal.reduce((sum, row) => sum + (row.kind === "gain" ? row.amount : -row.amount), 0);

/* ---------- Account store: Échanges facade + host values (theme, level), one stable snapshot ---------- */

type HostExtras = { theme: ThemePreference; level?: string };
let hostExtras: HostExtras = { theme: "system" };
const extrasListeners = new Set<() => void>();
const setHostExtras = (patch: Partial<HostExtras>) => {
  if ((Object.keys(patch) as (keyof HostExtras)[]).every(k => hostExtras[k] === patch[k])) return;
  hostExtras = { ...hostExtras, ...patch };
  extrasListeners.forEach(l => l());
};
/** Root callbacks registered by App (theme setter, router): actions call them, never a copy of their state. */
const hostCallbacks: { setTheme?: (theme: ThemePreference) => void; navigate?: (route: Route) => void } = {};

let accountSnap: AccountExperienceSnapshot | null = null;
let accountKeys: { ex: ExchangeAccountState; x: HostExtras } | null = null;
const accountStore: AccountExperienceStore = {
  subscribe: listener => {
    const off = exchangeAccountSource.subscribe(listener);
    extrasListeners.add(listener);
    return () => { off(); extrasListeners.delete(listener); };
  },
  getSnapshot: () => {
    const ex = exchangeAccountSource.getState(), x = hostExtras;
    if (!accountSnap || !accountKeys || accountKeys.ex !== ex || accountKeys.x !== x) {
      accountKeys = { ex, x };
      accountSnap = {
        profile: { ...ex.profile, level: x.level }, balances: ex.balances, acquisitions: ex.acquisitions, requests: ex.requests,
        notifications: ex.notifications, milesJournal: MILES_JOURNAL, theme: x.theme,
      };
    }
    return accountSnap;
  },
  actions: {
    ...exchangeAccountSource.actions,
    // Booking stays in Coaching (same credits and appointments): open its Consultations view, where the slot is chosen.
    bookService: () => {
      hostCallbacks.navigate?.("accompagnement");
      window.history.replaceState(null, "", "#/accompagnement?view=consult");
      window.dispatchEvent(new PopStateEvent("popstate"));
    },
    setTheme: theme => hostCallbacks.setTheme?.(theme),
  },
};
connectAccountExperienceStore(accountStore);
const ACCOUNT_ROUTES = new Set<Route>(["shop", "account", "miles"]);

const notifications: NotificationItem[] = [
  { id: "team-t1", title: "Préparer le hot seat de mardi", description: "Équipe : la prochaine séance Team Genius porte aussi là-dessus, si tu veux arriver avec des pistes.", time: "9 h 16", tone: "info" },
  { id: "news-n1", title: "Le point du soir, en une minute", description: "Depuis Objectifs, « Faire le point du soir » réunit tes résultats et tes routines du jour : tu relèves en quelques gestes.", time: "il y a 2 h", tone: "info" },
  { id: "news-n2", title: "Une routine peut accompagner un objectif", description: "À la création d’un objectif, « Ajouter une routine » est facultatif.", time: "hier", tone: "info" },
  { id: "team-t2", title: "Changer le jour de mes séances de coaching", description: "Équipe : c’est noté. On regarde avec ton coach les créneaux possibles et on te répond ici.", time: "lun.", read: true, tone: "success" },
  { id: "news-p6", title: "Deux replays ajoutés dans Apprendre", description: "Cette semaine : deux replays ajoutés dans Apprendre, en Vente et en Marketing. Le hot seat de mardi a encore des places.", time: "mar.", read: true, tone: "info" },
  { id: "news-n3", title: "Ton espace Liberty Coach", description: "Retrouve tes documents et ton suivi dans Liberty Coach.", time: "lun.", read: true, tone: "info" },
  { id: "news-p0", title: "Bienvenue : ce qu’on partage ici", description: "Ce fil réunit les membres Team Coaching. Partage ce qui t’aide, pose tes questions, salue les réussites des autres.", time: "lun.", read: true, tone: "info" },
];

const contentCommands: CommandItem[] = [
  { id: "team:t1", label: "Préparer le hot seat de mardi", description: "Message de l’équipe · 9 h 16", group: "Messages", keywords: ["hot seat", "équipe"] },
  { id: "team:t2", label: "Changer le jour de mes séances de coaching", description: "Message de l’équipe · lun.", group: "Messages", keywords: ["séances", "coach"] },
  { id: "team:t3", label: "Modèles de relance du replay", description: "Message de l’équipe · 15 sept.", group: "Messages", keywords: ["replay", "relance"] },
  { id: "community:n1", label: "Le point du soir, en une minute", description: "Actualité · il y a 2 h", group: "Actualités" },
  { id: "community:n2", label: "Une routine peut accompagner un objectif", description: "Actualité · hier", group: "Actualités" },
  { id: "community:p6", label: "Deux replays ajoutés dans Apprendre", description: "Actualité · mar.", group: "Actualités" },
];
const commands = [...routeCommands, ...contentCommands];

/* ---------- Theme ---------- */

/**
 * TC charter, centralised: every NON-status semantic key of tokens.json, from the TC neutral palette
 * (prototype-tc-reprise/arc/upstream/registry/foundation.css). Status keys (danger, dangerSoft, success, warning) stay the
 * vendored defaults. DM Sans, radius 28 and motion stay vendored.
 */
const TC_THEME: ThemeConfig = {
  light: {
    canvas: "oklch(100% 0 0)", panel: "oklch(100% 0 0)", ink: "oklch(15% 0 0)", muted: "oklch(46% 0 0)",
    subtle: "oklch(97.8% 0 0)", border: "oklch(93.5% 0 0)", accent: "#111111", accentHover: "#2a2a2a",
    onAccent: "oklch(100% 0 0)", accentSoft: "oklch(33% 0 0 / .1)", focus: "oklch(33% 0 0)",
  },
  dark: {
    canvas: "#0a0a0a", panel: "#121212", ink: "#f2f2f2", muted: "#9a9a9a",
    subtle: "#191919", border: "#303030", accent: "#f2f2f2", accentHover: "#ffffff",
    onAccent: "#0a0a0a", accentSoft: "#ffffff0f", focus: "#f2f2f2",
  },
};
/** Raised surfaces have no semantic key in tokens.json; tc-theme.css binds --arc-surface-raised to these. */
const TC_RAISED = { light: "oklch(100% 0 0)", dark: "#1a1a1a" } as const;
const BASE_KEYS = new Set(["--font-family", "--font-scale", "--radius", "--touch-target"]);

/**
 * Stable per-mode aliases on <html> (--tc-light-panel, --tc-dark-panel…), all derived from createThemeVariables(TC_THEME).
 * tc-theme.css maps them into every light/dark island, so an island never falls back to the vendored blue palette.
 */
const TC_MODE_ALIASES: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const mode of ["light", "dark"] as const) {
    for (const [key, value] of Object.entries(createThemeVariables(TC_THEME, mode))) {
      if (!BASE_KEYS.has(key)) out[`--tc-${mode}-${key.slice(2)}`] = String(value);
    }
    out[`--tc-${mode}-raised`] = TC_RAISED[mode];
  }
  return out;
})();
let appliedThemeKeys: string[] = [];

const THEME_KEY = "tc-proto-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";
const readTheme = (): ThemePreference => { try { const v = localStorage.getItem(THEME_KEY); return v === "light" || v === "dark" ? v : "system"; } catch { return "system"; } };

export function App() {
  const [route, setRoute] = useState<Route>(readRoute);
  const [theme, setTheme] = useState<ThemePreference>(readTheme);
  const systemDark = useMediaQuery(DARK_QUERY);
  const mobile = useMediaQuery(MOBILE_QUERY);
  const resolvedTheme = theme === "system" ? (systemDark ? "dark" : "light") : theme;
  const [peek, setPeek] = useState<PeekDetail | null>(null);
  const [peekOpen, setPeekOpen] = useState(false);
  const routeChanged = useRef(false);

  // Hash is the router. External changes (back/forward, typed or shared link) close the previous detail, then follow the URL.
  // In-app navigation uses pushState, which fires neither event, so a detail opened right after navigate() stays open.
  useEffect(() => {
    const onExternal = () => {
      setPeekOpen(false);
      const next = readRoute();
      setRoute(current => { if (current !== next) routeChanged.current = true; return next; });
    };
    window.addEventListener("hashchange", onExternal);
    window.addEventListener("popstate", onExternal);
    return () => { window.removeEventListener("hashchange", onExternal); window.removeEventListener("popstate", onExternal); };
  }, []);

  // A new page starts at its top and takes the focus for screen readers. Closing the detail belongs to the navigation
  // gesture itself (navigate / external handler), never to this effect: it would close a detail opened after navigate().
  // A detail opened in the same gesture keeps the focus: focusing main would count as a focus outside the non-modal peek
  // and close it.
  const peekOpenRef = useRef(peekOpen);
  peekOpenRef.current = peekOpen;
  useEffect(() => {
    if (!routeChanged.current) return;
    routeChanged.current = false;
    const main = document.getElementById("tc-main");
    main?.scrollTo({ top: 0 });
    if (!peekOpenRef.current) main?.focus({ preventScroll: true });
  }, [route]);

  // Theme variables and data-theme on <html> so portals (dialogs, popovers, sheets) share the tokens.
  useEffect(() => {
    const html = document.documentElement;
    const variables = { ...createThemeVariables(TC_THEME, resolvedTheme), ...TC_MODE_ALIASES };
    html.classList.add("tc-charte");
    for (const key of appliedThemeKeys) if (!(key in variables)) html.style.removeProperty(key);
    for (const [key, value] of Object.entries(variables)) html.style.setProperty(key, String(value));
    appliedThemeKeys = Object.keys(variables);
    html.dataset.theme = resolvedTheme;
    html.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  const changeTheme = useCallback((next: ThemePreference) => {
    setTheme(next);
    try { if (next === "system") localStorage.removeItem(THEME_KEY); else localStorage.setItem(THEME_KEY, next); } catch { /* Not kept in private mode. */ }
  }, []);

  // Synchronous: the previous detail is closed and the route set before returning, so a caller may chain openPeek().
  // Same route: only the previous detail closes; focus stays where the gesture happened (a following openPeek takes it).
  // A route root clears the page query: from #/learn?level=… a navigate("learn") lands on the Learn home. The page's own
  // location hook only listens to hashchange/popstate, so one popstate is dispatched in that case (App's handler then reads
  // the same route: no loop).
  const navigate = useCallback((next: Route) => {
    setPeekOpen(false);
    const same = readRoute() === next;
    if (same && !hashHasQuery()) return;
    window.history.pushState(null, "", `#/${next}`);
    if (same) { window.dispatchEvent(new PopStateEvent("popstate")); return; }
    routeChanged.current = true;
    setRoute(next);
  }, []);
  // Course reader state reported by LearnExperience (setState: stable identity, no effect loop).
  const [readerActive, setReaderActive] = useState(false);
  const onReaderChange = useCallback((active: boolean) => setReaderActive(active), []);

  const openPeek = useCallback((detail: PeekDetail) => { setPeek(detail); setPeekOpen(true); }, []);

  // Account store host values: the real theme setter/router, the current theme and the live learning level.
  const level = levelOfLesson(useActivities().learn.last);
  useEffect(() => { hostCallbacks.setTheme = changeTheme; hostCallbacks.navigate = navigate; }, [changeTheme, navigate]);
  useEffect(() => { setHostExtras({ theme, level }); }, [theme, level]);
  // Shell profile card follows the same snapshot (name, status, level, Miles from the journal) after every mutation.
  const account = useSyncExternalStore(accountStore.subscribe, accountStore.getSnapshot, accountStore.getSnapshot);
  const person: ShellPerson = useMemo(() => ({
    name: account.profile.name, status: account.profile.status, level: account.profile.level, miles: milesBalance(account.milesJournal), pieces: account.balances,
  }), [account]);
  const openPurchases = useCallback(() => openAccountSection(navigate, "purchases"), [navigate]);

  const onCommand = useCallback((item: CommandItem) => {
    const target = item.id.split(":")[0] === "route" ? item.id.slice("route:".length) : item.id.split(":")[0]!;
    if (isRoute(target)) navigate(target);
  }, [navigate]);

  const pageProps: PageProps = { route, navigate, openPeek };

  return <>
    <Shell route={route} navigate={navigate} mobile={mobile} peekOpen={peekOpen} theme={theme} resolvedTheme={resolvedTheme} onThemeChange={changeTheme}
      person={person} notifications={notifications} commands={commands} onCommand={onCommand} reader={route === "learn" && readerActive} onOpenPurchases={openPurchases}>
      {route === "learn" ? <LearnExperience {...pageProps} onReaderChange={onReaderChange} />
        : route === "accompagnement" ? <CoachingExperience {...pageProps} />
        : ACCOUNT_ROUTES.has(route) ? <ShopAccountExperience {...pageProps} />
        : ACTIVITY_ROUTES.has(route) ? <ActivitiesPages {...pageProps} /> : <ExchangePages {...pageProps} />}
    </Shell>
    <SharedPeek detail={peek} open={peekOpen} onOpenChange={setPeekOpen} mobile={mobile} />
  </>;
}

const mount = document.getElementById("root");
if (!mount) throw new Error("[TC prototype] #root is missing from index.html.");
createRoot(mount).render(<StrictMode><App /></StrictMode>);
